import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, GripVertical, Eye, EyeOff } from "lucide-react";

interface TeamMember {
  id: string;
  name: string;
  position: string;
  bio: string;
  photo_url: string | null;
  linkedin_url: string | null;
  email: string;
  slug: string;
  status: string;
  sort_order: number;
}

const emptyMember = {
  name: "",
  position: "",
  bio: "",
  photo_url: "",
  linkedin_url: "",
  email: "",
  slug: "",
  status: "active",
  sort_order: 0,
};

const TeamManagement = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Partial<TeamMember> | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchMembers = async () => {
    const { data, error } = await supabase
      .from("team_members")
      .select("*")
      .order("sort_order");
    if (error) {
      toast({ title: "Error loading team members", description: error.message, variant: "destructive" });
    } else {
      setMembers(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const generateSlug = (name: string) => name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const openCreate = () => {
    setEditingMember({ ...emptyMember, sort_order: members.length });
    setDialogOpen(true);
  };

  const openEdit = (member: TeamMember) => {
    setEditingMember({ ...member });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingMember?.name || !editingMember?.position || !editingMember?.bio || !editingMember?.email) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setSaving(true);
    const slug = editingMember.slug || generateSlug(editingMember.name);
    const payload = {
      name: editingMember.name,
      position: editingMember.position,
      bio: editingMember.bio,
      photo_url: editingMember.photo_url || null,
      linkedin_url: editingMember.linkedin_url || null,
      email: editingMember.email,
      slug,
      status: editingMember.status || "active",
      sort_order: editingMember.sort_order ?? members.length,
    };

    let error;
    if (editingMember.id) {
      ({ error } = await supabase.from("team_members").update(payload).eq("id", editingMember.id));
    } else {
      ({ error } = await supabase.from("team_members").insert(payload));
    }

    if (error) {
      toast({ title: "Error saving", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editingMember.id ? "Team member updated" : "Team member created" });
      setDialogOpen(false);
      setEditingMember(null);
      fetchMembers();
    }
    setSaving(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete ${name}?`)) return;
    const { error } = await supabase.from("team_members").delete().eq("id", id);
    if (error) {
      toast({ title: "Error deleting", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Team member deleted" });
      fetchMembers();
    }
  };

  const toggleStatus = async (member: TeamMember) => {
    const newStatus = member.status === "active" ? "inactive" : "active";
    const { error } = await supabase.from("team_members").update({ status: newStatus }).eq("id", member.id);
    if (error) {
      toast({ title: "Error updating status", description: error.message, variant: "destructive" });
    } else {
      fetchMembers();
    }
  };

  const updateField = (field: string, value: string | number) => {
    setEditingMember(prev => prev ? { ...prev, [field]: value } : prev);
  };

  return (
    <div className="py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Team Management</h1>
            <p className="text-muted-foreground mt-1">Manage the team members displayed on the website.</p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Order</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : members.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No team members found.</TableCell></TableRow>
                ) : (
                  members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          {member.sort_order}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          {member.photo_url ? (
                            <img src={member.photo_url} alt={member.name} className="h-8 w-8 rounded-full object-cover" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                              {member.name.split(" ").map(n => n[0]).join("")}
                            </div>
                          )}
                          {member.name}
                        </div>
                      </TableCell>
                      <TableCell>{member.position}</TableCell>
                      <TableCell>
                        <Badge
                          variant={member.status === "active" ? "default" : "secondary"}
                          className="cursor-pointer"
                          onClick={() => toggleStatus(member)}
                        >
                          {member.status === "active" ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEdit(member)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(member.id, member.name)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMember?.id ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" value={editingMember?.name || ""} onChange={e => { updateField("name", e.target.value); if (!editingMember?.id) updateField("slug", generateSlug(e.target.value)); }} />
                </div>
                <div>
                  <Label htmlFor="position">Position *</Label>
                  <Input id="position" value={editingMember?.position || ""} onChange={e => updateField("position", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={editingMember?.email || ""} onChange={e => updateField("email", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" value={editingMember?.slug || ""} onChange={e => updateField("slug", e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="bio">Biography *</Label>
                <Textarea id="bio" rows={5} value={editingMember?.bio || ""} onChange={e => updateField("bio", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="photo_url">Photo URL</Label>
                  <Input id="photo_url" value={editingMember?.photo_url || ""} onChange={e => updateField("photo_url", e.target.value)} placeholder="/lovable-uploads/..." />
                </div>
                <div>
                  <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                  <Input id="linkedin_url" value={editingMember?.linkedin_url || ""} onChange={e => updateField("linkedin_url", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sort_order">Display Order</Label>
                  <Input id="sort_order" type="number" value={editingMember?.sort_order ?? 0} onChange={e => updateField("sort_order", parseInt(e.target.value) || 0)} />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={editingMember?.status || "active"}
                    onChange={e => updateField("status", e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : editingMember?.id ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeamManagement;
